import type {
  AppBootstrap,
  CatalogPage,
  CatalogQuery,
  HistoryItem,
  ProviderPublicConfig,
  QueueItem,
  SongDetail,
} from '@pipi/shared';

const configuredOrigin = import.meta.env.VITE_API_ORIGIN?.replace(/\/$/, '') || '';
const adminTokenKey = 'pipi:admin-token';

function adminHeaders(): Record<string, string> {
  const token = window.localStorage.getItem(adminTokenKey)?.trim();
  return token ? { 'X-Pipi-Admin-Token': token } : {};
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${configuredOrigin}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(payload?.error || `请求失败（${response.status}）`, response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

function queryString(query: CatalogQuery) {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== 'all' && value !== false) params.set(key, String(value));
  });
  const value = params.toString();
  return value ? `?${value}` : '';
}

export const api = {
  bootstrap: () => request<AppBootstrap>('/api/bootstrap'),
  catalog: (query: CatalogQuery = {}) => request<CatalogPage>(`/api/catalog${queryString(query)}`),
  song: (id: string) => request<SongDetail>(`/api/songs/${encodeURIComponent(id)}`),
  history: () => request<HistoryItem[]>('/api/history'),
  providers: () => request<ProviderPublicConfig[]>('/api/providers'),
  updateProvider: (id: string, update: { enabled?: boolean; name?: string; config?: Record<string, unknown> }) =>
    request<ProviderPublicConfig>(`/api/providers/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: adminHeaders(),
      body: JSON.stringify(update),
    }),
  testProvider: (id: string) =>
    request<{ ok: boolean; message: string }>(`/api/providers/${encodeURIComponent(id)}/test`, {
      method: 'POST',
      headers: adminHeaders(),
    }),
  syncProvider: (id: string) =>
    request<{ providerId: string; count: number; lastSyncAt: string }>(`/api/providers/${encodeURIComponent(id)}/sync`, {
      method: 'POST',
      headers: adminHeaders(),
    }),
  addQueue: (songId: string, singer: string) =>
    request<QueueItem>('/api/queue', { method: 'POST', body: JSON.stringify({ songId, singer }) }),
  removeQueue: (id: string) => request<void>(`/api/queue/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  moveQueue: (id: string, direction: -1 | 1) =>
    request<QueueItem[]>(`/api/queue/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ direction }) }),
  queueStatus: (id: string, status: 'playing' | 'completed', score?: number) =>
    request<QueueItem[]>(`/api/queue/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ status, score }) }),
  favorite: (songId: string, favorite: boolean) =>
    request<{ songId: string; favorite: boolean }>(`/api/favorites/${encodeURIComponent(songId)}`, {
      method: 'PUT',
      body: JSON.stringify({ favorite }),
    }),
  getAdminToken: () => window.localStorage.getItem(adminTokenKey) || '',
  setAdminToken: (token: string) => {
    const normalized = token.trim();
    if (normalized) window.localStorage.setItem(adminTokenKey, normalized);
    else window.localStorage.removeItem(adminTokenKey);
  },
};

export function websocketUrl() {
  if (configuredOrigin) return `${configuredOrigin.replace(/^http/, 'ws')}/ws`;
  return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
}
